import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import PageHeader from "../components/common/PageHeader";
import LocationMapView from "../components/common/LocationMapView";
import GoogleBusinessSpreadsheetView from "../components/GoogleBusinessSpreadsheetView";
import LocationHistoryModal from "../components/LocationHistoryModal";
import CreateOrLinkMasterLocationModal from "../components/CreateOrLinkMasterLocationModal";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications";
import { useDebounce } from "../hook/useDebounce";
import { Icon } from "@iconify/react";

const LocationsGoogleBusinessPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmedFilter, setConfirmedFilter] = useState("ALL");
  const [counts, setCounts] = useState({ all: 0, confirmed: 0, not_confirmed: 0, pending: 0 });
  const [togglingId, setTogglingId] = useState(null);

  // View Mode: 'table', 'sheet', or 'map'
  const [viewMode, setViewMode] = useState("table");

  // Version History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLocation, setHistoryLocation] = useState(null);

  // Create / Link in Master Data Modal State
  const [createLinkModalOpen, setCreateLinkModalOpen] = useState(false);
  const [selectedGbpForMaster, setSelectedGbpForMaster] = useState(null);

  // Modal & Account State
  const [showImportModal, setShowImportModal] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [importing, setImporting] = useState(false);

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "title", direction: "asc" });
  const debouncedSearch = useDebounce(search, 500);

  const isRowConfirmed = useCallback((row) => {
    if (typeof row?.is_confirmed === "boolean") return row.is_confirmed;
    const val = String(row?.is_verified ?? "").toUpperCase();
    return val === "VERIFIED" || val === "CONFIRMED" || val === "TRUE" || val === "1";
  }, []);

  const getRowConfirmationState = useCallback((row) => {
    if (row?.confirmation_status === "PENDING" || String(row?.is_verified || "").toUpperCase() === "PENDING") {
      return "PENDING";
    }
    return isRowConfirmed(row) ? "CONFIRMED" : "NOT_CONFIRMED";
  }, [isRowConfirmed]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isFullView = viewMode === "map" || viewMode === "sheet";
      const res = await api.get("/google-business/locations", {
        params: { 
          search: debouncedSearch, 
          page: isFullView ? 1 : page, 
          limit: isFullView ? 250 : limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
          confirmed: confirmedFilter,
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
      if (res.data?.counts) {
        setCounts(res.data.counts);
      }
    } catch (error) {
      showToast("error", "Failed to load Google Business records");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig, viewMode, confirmedFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleConfirmation = useCallback(async (row, e) => {
    if (e) e.stopPropagation();
    if (!row?.id || togglingId === row.id) return;
    const currentlyConfirmed = isRowConfirmed(row);
    const nextConfirmed = !currentlyConfirmed;
    setTogglingId(row.id);
    try {
      const res = await api.patch(`/google-business/locations/${row.id}/confirmation`, {
        is_confirmed: nextConfirmed,
      });
      if (res.data?.success) {
        showToast(
          "success",
          `"${row.title || row.store_code || "Location"}" marked as ${nextConfirmed ? "Confirmed" : "Not Confirmed"}`
        );
        fetchData();
      }
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to update confirmation status");
    } finally {
      setTogglingId(null);
    }
  }, [isRowConfirmed, togglingId, fetchData]);

  const loadSavedAccounts = async () => {
    try {
      const res = await api.get("/google-business/accounts");
      if (res.data?.success) {
        setIsConnected(res.data.isConnected);
        const savedAccounts = res.data.accounts || [];
        setAccounts(savedAccounts);
        if (savedAccounts.length > 0) {
          setSelectedAccount(savedAccounts[0].accountId);
        }
      }
    } catch (error) {
      console.error("Could not fetch saved accounts:", error);
    }
  };

  const handleOpenModal = () => {
    setShowImportModal(true);
    loadSavedAccounts();
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await api.get("/google-business/connect-url");
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      showToast("error", "Failed to get Google authorization URL.");
    }
  };

  const handleRefreshAccounts = async () => {
    setFetchingAccounts(true);
    try {
      const res = await api.post("/google-business/fetch-accounts");
      if (res.data?.success) {
        const fetched = res.data.accounts || [];
        setAccounts(fetched);
        if (fetched.length > 0) setSelectedAccount(fetched[0].accountId);
        showToast("success", `Found ${fetched.length} account(s)!`);
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to fetch accounts.");
    } finally {
      setFetchingAccounts(false);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAccount) {
      showToast("error", "Please select a Google Business account.");
      return;
    }

    setImporting(true);
    try {
      const res = await api.post("/google-business/import-locations", {
        accountId: selectedAccount,
      });

      if (res.data?.success) {
        showToast("success", `Imported ${res.data.data.totalFetched} profile(s) successfully!`);
        setShowImportModal(false);
        fetchData();
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to import locations.");
    } finally {
      setImporting(false);
    }
  };

  const columns = useMemo(() => [
    {
      key: "title",
      label: "Name",
      category: "General",
      defaultVisible: true,
      className: "text-xs fw-semibold",
      sortable: true,
      render: (row) => {
        const confState = getRowConfirmationState(row);
        return (
          <div className="d-inline-flex align-items-center gap-1">
            <Icon
              icon={
                confState === "CONFIRMED"
                  ? "solar:verified-check-bold"
                  : confState === "PENDING"
                    ? "solar:clock-circle-bold"
                    : "solar:danger-circle-bold"
              }
              className={`text-sm flex-shrink-0 ${
                confState === "CONFIRMED"
                  ? "text-success-main"
                  : confState === "PENDING"
                    ? "text-warning-main"
                    : "text-danger-main"
              }`}
              title={
                confState === "CONFIRMED"
                  ? "Confirmed in Google Business Profile"
                  : confState === "PENDING"
                    ? "Pending Confirmation in Google Business Profile"
                    : "Not Confirmed in Google Business Profile"
              }
            />
            <span
              className="text-primary-600 pointer hover-text-decoration-underline"
              onClick={() => navigate(`/google-business/${row.id}/view`)}
            >
              {row.title || row.store_code || "Unnamed Business"}
            </span>
          </div>
        );
      },
    },
    { 
      key: "store_code", 
      label: "Store Code", 
      category: "General",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.store_code || "—"
    },
    {
      key: "linked_location_id",
      label: "Master Data",
      category: "General",
      defaultVisible: true,
      sortable: false,
      render: (row) =>
        row.linked_location_id ? (
          <div className="d-inline-flex align-items-center gap-1">
            <span
              className="badge bg-success-focus text-success-main px-8 py-4 radius-6 text-xs fw-medium pointer d-inline-flex align-items-center gap-1"
              onClick={() => navigate(`/locations-main/${row.linked_location_id}/edit`)}
              title={`Open "${row.linked_location_name || "Master Location"}" in Master Data`}
            >
              <Icon icon="solar:link-circle-bold" className="text-sm" />
              <span>Linked ({row.linked_location_name || `#${row.linked_location_id}`})</span>
            </span>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-xs btn-outline-primary radius-6 px-8 py-2 text-xs fw-semibold d-inline-flex align-items-center gap-1"
            onClick={() => {
              setSelectedGbpForMaster(row);
              setCreateLinkModalOpen(true);
            }}
            title="Create in Master Data or Link to existing Master Location"
          >
            <Icon icon="solar:map-point-add-linear" className="text-sm" />
            <span>Create / Link</span>
          </button>
        ),
    },
    { 
      key: "primary_category_name", 
      label: "Category", 
      category: "General",
      defaultVisible: true,
      sortable: true,
      render: (row) => row.primary_category_name || "—"
    },
    { 
      key: "status",
      label: "Status",
      category: "General",
      defaultVisible: true,
      sortable: true,
      render: (row) => {
        const status = row.open_status || row.status || "OPEN";
        const isOk = status === "OPEN" || status === "SYNCED";

        return (
          <span
            className={`badge px-8 py-2 radius-4 fw-medium text-xs ${
              isOk
                ? "bg-success-focus text-success-main"
                : "bg-warning-focus text-warning-main"
            }`}
          >
            {status}
          </span>
        );
      }
    },
    { 
      key: "is_verified", 
      label: "Confirmed", 
      category: "General",
      defaultVisible: true,
      sortable: true,
      render: (row) => {
        const confState = getRowConfirmationState(row);
        const isBusy = togglingId === row.id;
        return (
          <button
            type="button"
            disabled={isBusy}
            onClick={(e) => handleToggleConfirmation(row, e)}
            title={
              confState === "CONFIRMED"
                ? "Location is Confirmed. Click to mark as Not Confirmed."
                : "Location is Not Confirmed. Click to mark as Confirmed."
            }
            className={`badge border-0 px-10 py-4 radius-6 text-xs fw-semibold d-inline-flex align-items-center gap-1 pointer ${
              confState === "CONFIRMED"
                ? "bg-success-focus text-success-main"
                : confState === "PENDING"
                  ? "bg-warning-focus text-warning-main"
                  : "bg-danger-focus text-danger-main"
            }`}
          >
            <Icon
              icon={
                isBusy
                  ? "svg-spinners:180-ring"
                  : confState === "CONFIRMED"
                    ? "solar:verified-check-bold"
                    : confState === "PENDING"
                      ? "solar:clock-circle-bold"
                      : "solar:close-circle-bold"
              }
              className="text-sm"
            />
            <span>
              {confState === "CONFIRMED"
                ? "Confirmed"
                : confState === "PENDING"
                  ? "Pending"
                  : "Not Confirmed"}
            </span>
          </button>
        );
      }
    },
    { 
      key: "address_line1", 
      label: "Address Line 1", 
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.address_line1 || "—"
    },
    { 
      key: "postal_code", 
      label: "Postal Code", 
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.postal_code || "—"
    },
    { 
      key: "locality", 
      label: "City", 
      category: "Address",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.locality || row.city || "—"
    },
    { 
      key: "administrative_area", 
      label: "State / Region", 
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.administrative_area || "—"
    },
    { 
      key: "country_code", 
      label: "Country", 
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.country_code || "—"
    },
    { 
      key: "primary_phone", 
      label: "Phone", 
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.primary_phone || "—"
    },
    { 
      key: "website_uri", 
      label: "Website", 
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.website_uri ? (
        <a href={row.website_uri} target="_blank" rel="noreferrer" className="text-primary-600 text-decoration-none text-xs">
          {row.website_uri.replace(/^https?:\/\//, '')}
        </a>
      ) : "—"
    },
    { 
      key: "maps_uri", 
      label: "Google Maps URL", 
      category: "Links",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.maps_uri ? (
        <a href={row.maps_uri} target="_blank" rel="noreferrer" className="text-primary-600 text-xs">
          Open in Maps
        </a>
      ) : "—"
    },
    { 
      key: "version_number", 
      label: "Version", 
      category: "System",
      defaultVisible: true,
      sortable: true,
      render: (row) => (
        <span
          className="badge bg-primary-50 text-primary-600 border border-primary-200 radius-4 px-8 py-2 text-xs pointer d-inline-flex align-items-center gap-1"
          title="Click to view version history (Showing latest version)"
          onClick={() => {
            setHistoryLocation(row);
            setHistoryModalOpen(true);
          }}
        >
          <span>v{row.version_number || 1}</span>
          {row.total_versions > 1 && (
            <span className="text-secondary-light fw-normal">({row.total_versions})</span>
          )}
        </span>
      )
    },
    { 
      key: "updated_at", 
      label: "Last Modified", 
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.updated_at ? new Date(row.updated_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }) : "—"
    }
  ], [navigate, getRowConfirmationState, togglingId, handleToggleConfirmation]);

  return (
    <>
      <PageHeader
        title="Google Business Profile"
        subtitle="Manage and synchronize Google Business locations."
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary-600 radius-8 px-16 py-8 d-flex align-items-center gap-2 text-sm fw-semibold shadow-sm"
              onClick={() => {
                setSelectedGbpForMaster(null);
                setCreateLinkModalOpen(true);
              }}
            >
              <Icon icon="solar:map-point-add-bold" className="text-base" />
              <span>Create / Link in Master Data</span>
            </button>

            <button
              type="button"
              className="btn btn-outline-primary radius-8 px-16 py-8 d-flex align-items-center gap-2 text-sm fw-semibold shadow-sm"
              onClick={handleOpenModal}
            >
              <Icon icon="logos:google-icon" className="text-base" />
              <span>Import from Google</span>
            </button>

            <div className="btn-group radius-8 overflow-hidden border">
              <button
                className={`btn btn-sm ${viewMode === "table" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("table")}
              >
                <Icon icon="ri:table-line" className="me-1 text-base" /> Table
              </button>
              <button
                className={`btn btn-sm ${viewMode === "sheet" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("sheet")}
              >
                <Icon icon="ri:file-excel-2-line" className="me-1 text-base" /> Sheet View
              </button>
              <button
                className={`btn btn-sm ${viewMode === "map" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("map")}
              >
                <Icon icon="ri:map-pin-line" className="me-1 text-base" /> Map View
              </button>
            </div>
          </>
        }
      />

      {/* Confirmation Status Filter Bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="text-xs fw-semibold text-secondary-light me-1">Confirmation Status:</span>
          <button
            type="button"
            onClick={() => {
              setConfirmedFilter("ALL");
              setPage(1);
            }}
            className={`btn btn-sm radius-8 px-12 py-6 d-inline-flex align-items-center gap-2 text-xs fw-semibold ${
              confirmedFilter === "ALL"
                ? "btn-primary-600 shadow-sm"
                : "btn-light border text-secondary-light"
            }`}
          >
            <Icon icon="solar:buildings-2-bold" className="text-sm" />
            <span>All Locations</span>
            <span className={`badge radius-4 px-6 py-2 text-xs ${confirmedFilter === "ALL" ? "bg-white text-primary-600" : "bg-neutral-200 text-neutral-700"}`}>
              {counts.all}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setConfirmedFilter("CONFIRMED");
              setPage(1);
            }}
            className={`btn btn-sm radius-8 px-12 py-6 d-inline-flex align-items-center gap-2 text-xs fw-semibold ${
              confirmedFilter === "CONFIRMED"
                ? "btn-success shadow-sm text-white"
                : "btn-light border text-success-main"
            }`}
          >
            <Icon icon="solar:verified-check-bold" className="text-sm" />
            <span>Confirmed</span>
            <span className={`badge radius-4 px-6 py-2 text-xs ${confirmedFilter === "CONFIRMED" ? "bg-white text-success-main" : "bg-success-focus text-success-main"}`}>
              {counts.confirmed}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setConfirmedFilter("NOT_CONFIRMED");
              setPage(1);
            }}
            className={`btn btn-sm radius-8 px-12 py-6 d-inline-flex align-items-center gap-2 text-xs fw-semibold ${
              confirmedFilter === "NOT_CONFIRMED"
                ? "btn-danger shadow-sm text-white"
                : "btn-light border text-danger-main"
            }`}
          >
            <Icon icon="solar:close-circle-bold" className="text-sm" />
            <span>Not Confirmed</span>
            <span className={`badge radius-4 px-6 py-2 text-xs ${confirmedFilter === "NOT_CONFIRMED" ? "bg-white text-danger-main" : "bg-danger-focus text-danger-main"}`}>
              {counts.not_confirmed}
            </span>
          </button>
        </div>

        <div className="text-xs text-secondary-light d-flex align-items-center gap-1">
          <Icon icon="solar:info-circle-linear" className="text-sm text-primary-600" />
          <span>Click any badge in the <strong>Confirmed</strong> column to mark a location as Confirmed or Not Confirmed.</span>
        </div>
      </div>

      {viewMode === "table" ? (
        <DataTable
          storageKey="loqation_table_columns_gbp_v1"
          data={data}
          loading={loading}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          showCheckbox={false}
          sortConfig={sortConfig}
          onSortChange={(config) => {
            setSortConfig(config);
            setPage(1);
          }}
          columns={columns}

          actions={[
            {
              icon: "solar:map-point-add-linear",
              className: "w-28-px h-28-px bg-primary-50 text-primary-600 rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => {
                setSelectedGbpForMaster(row);
                setCreateLinkModalOpen(true);
              },
              title: "Create or Link in Master Data",
            },
            {
              icon: "lucide:edit",
              className: "w-28-px h-28-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => navigate(`/google-business/${row.id}/view`),
              title: "View Profile Details",
            },
            {
              icon: "solar:history-linear",
              className: "w-28-px h-28-px bg-info-focus text-info-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => {
                setHistoryLocation(row);
                setHistoryModalOpen(true);
              },
              title: "View Version History",
            },
            {
              icon: "mingcute:delete-2-line",
              className: "w-28-px h-28-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => {
                confirmDelete(row.title || "Google Business profile", async () => {
                  try {
                    await api.delete(`/google-business/locations/${row.id}`);
                    showToast("success", "Profile deleted");
                    fetchData();
                  } catch {
                    showToast("error", "Delete failed");
                  }
                });
              },
              title: "Delete Profile",
            },
          ]}
        />
      ) : viewMode === "sheet" ? (
        <GoogleBusinessSpreadsheetView
          data={data}
          loading={loading}
          onRefresh={fetchData}
          onOpenHistory={(row) => {
            setHistoryLocation(row);
            setHistoryModalOpen(true);
          }}
        />
      ) : (
        <LocationMapView 
          locations={data} 
          loading={loading}
          allowPinDrag={false}
          onRefresh={fetchData}
          getViewRoute={(item) => `/google-business/${item.id}/view`} 
        />
      )}

      {/* Account Selection Modal */}
      {showImportModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content radius-12 p-16">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Import Business Profiles</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                {!isConnected ? (
                  <div className="text-center py-20">
                    <p className="text-secondary-light text-sm mb-16">
                      Your Google account is not connected yet. Click below to authorize access.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary-600 radius-8 px-20 py-10 d-inline-flex align-items-center gap-2"
                      onClick={handleConnectGoogle}
                    >
                      <i className="ri-google-fill text-lg" />
                      Connect Google Account
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleImportSubmit}>
                    <div className="mb-20">
                      <div className="d-flex justify-content-between align-items-center mb-8">
                        <label className="form-label fw-semibold text-sm mb-0">
                          Select Business Account
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary radius-8 text-xs d-flex align-items-center gap-1"
                          onClick={handleRefreshAccounts}
                          disabled={fetchingAccounts}
                        >
                          <i className="ri-refresh-line" />
                          {fetchingAccounts ? "Fetching..." : "Fetch Accounts"}
                        </button>
                      </div>

                      {accounts.length > 0 ? (
                        <select
                          className="form-select radius-8 text-sm"
                          value={selectedAccount}
                          onChange={(e) => setSelectedAccount(e.target.value)}
                        >
                          {accounts.map((acc) => (
                            <option key={acc.accountId} value={acc.accountId}>
                              {acc.accountName} ({acc.accountId}) - [{acc.type}]
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="alert alert-warning text-xs mb-0 radius-8">
                          No accounts found in DB. Click <strong>"Fetch Accounts"</strong> above to refresh accounts for your login.
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-24">
                      <button
                        type="button"
                        className="btn btn-neutral-200 text-neutral-600 radius-8"
                        onClick={() => setShowImportModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary-600 radius-8 d-flex align-items-center gap-2"
                        disabled={importing || !selectedAccount}
                      >
                        {importing ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Importing Profiles...
                          </>
                        ) : (
                          "Import Business Profiles"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {historyModalOpen && historyLocation && (
        <LocationHistoryModal
          isOpen={historyModalOpen}
          onClose={() => {
            setHistoryModalOpen(false);
            setHistoryLocation(null);
          }}
          endpoint={`/google-business/locations/${historyLocation.id}/history`}
          onRestore={async (item) => {
            const vNum = item?.version_number || item?.version;
            if (!vNum) return;
            try {
              const res = await api.post(
                `/google-business/locations/${historyLocation.id}/restore/${vNum}`
              );
              if (res.data?.success) {
                showToast(
                  "success",
                  res.data.message || `Restored version v${vNum} as latest`
                );
                setHistoryModalOpen(false);
                setHistoryLocation(null);
                fetchData();
              }
            } catch (err) {
              showToast(
                "error",
                err.response?.data?.error || "Failed to restore version"
              );
            }
          }}
        />
      )}

      {/* Create or Link in Master Data Modal */}
      <CreateOrLinkMasterLocationModal
        isOpen={createLinkModalOpen}
        onClose={() => {
          setCreateLinkModalOpen(false);
          setSelectedGbpForMaster(null);
        }}
        gbpLocation={selectedGbpForMaster}
        gbpLocationsList={data}
        onSuccess={() => {
          fetchData();
        }}
      />
    </>
  );
};

export default LocationsGoogleBusinessPage;